import { Box, Button as MuiButton } from '@mui/material';
import { ReactNode } from 'react';
import Icon from '../../IconSvg/Icon';
import { LelyButtonBaseProps } from '../buttonTypes';
import LelySpinner from '../LelySpinner/LelySpinner';

export type LelyLoadingButtonProps = LelyButtonBaseProps & {
  loading?: boolean;
  loadingIndicator?: ReactNode;
};

function LelyLoadingButton({
  inverted,
  dense,
  fullWidth,
  round,
  square,
  icon,
  leadingIcon,
  trailingIcon,
  startIcon,
  endIcon,
  loading = false,
  loadingIndicator,
  variant,
  'data-testid': dataTestId,
  children,
  ...props
}: LelyLoadingButtonProps) {
  const iconOnly = round || square;
  const iconOnlyName = icon ?? leadingIcon ?? trailingIcon;
  const buttonContent =
    iconOnly && iconOnlyName ? <Icon name={iconOnlyName} /> : children;
  const loadingIndicatorNode = loadingIndicator ?? (
    <LelySpinner variant={variant} />
  );
  const sx = [
    ...(loading ? [{ position: 'relative' as const }] : []),
    ...(Array.isArray(props.sx) ? props.sx : props.sx ? [props.sx] : []),
  ];

  return (
    <MuiButton
      {...props}
      variant={variant}
      fullWidth={fullWidth}
      startIcon={
        loading
          ? undefined
          : startIcon ??
            (!iconOnly && leadingIcon ? <Icon name={leadingIcon} /> : undefined)
      }
      endIcon={
        loading
          ? undefined
          : endIcon ??
            (!iconOnly && trailingIcon ? (
              <Icon name={trailingIcon} />
            ) : undefined)
      }
      data-inverted={inverted || undefined}
      data-dense={dense || undefined}
      data-full-width={fullWidth || undefined}
      data-icon-only={round || undefined}
      data-square-icon-only={!round && square ? true : undefined}
      data-loading={loading || undefined}
      data-testid={dataTestId}
      sx={sx}
    >
      {loading ? (
        <>
          <Box component="span" sx={{ visibility: 'hidden' }}>
            {buttonContent}
          </Box>
          <Box
            component="span"
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'inline-flex',
            }}
          >
            {loadingIndicatorNode}
          </Box>
        </>
      ) : (
        buttonContent
      )}
    </MuiButton>
  );
}

export default LelyLoadingButton;
